import React from 'react';

import './styles.scss';
import classNames from 'classnames';

import getUniqueId from '../../utils/getUniqueId';
import {DASHARRAY_LENGTH} from '../../constants';
import strings from '../../utils/strings';

class AudioPlayer extends React.Component {
	constructor() {
		super();

		this.uniqueId = `audio${getUniqueId()}`;
		this.animationFrame = null;
		this.isUnmounted = false;
		this.playRequestId = 0;

		this.state = {
			isPlaying: false,
			duration: 0,
			currentTime: 0,
			isDone: false,
			isStarted: false,
		};

		this.tick = this.tick.bind(this);
		this.handleStartPlayer = this.handleStartPlayer.bind(this);
		this.handlePause = this.handlePause.bind(this);
		this.handlePlay = this.handlePlay.bind(this);
		this.handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
		this.handleEnded = this.handleEnded.bind(this);
		this.handleError = this.handleError.bind(this);
	}

	componentDidMount() {
		this.isUnmounted = false;

		if (this.props.autoplay) {
			window.setTimeout(() => {
				if (!this.isUnmounted && this.audioRef) {
					this.handleStartPlayer();
				}
			}, 0);
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.src !== this.props.src && this.audioRef) {
			this.pauseCurrentAudio();
			this.audioRef.src = this.props.src;
			this.audioRef.load();

			if (this.props.autoplay) {
				window.setTimeout(() => {
					if (!this.isUnmounted && this.audioRef) {
						this.handleStartPlayer();
					}
				}, 0);
			}
		}
	}

	componentWillUnmount() {
		this.isUnmounted = true;
		this.playRequestId += 1;
		cancelAnimationFrame(this.animationFrame);

		if (this.audioRef) {
			try {
				this.audioRef.pause();
			} catch (error) {
				// Ignore browser audio cleanup errors.
			}
		}
	}

	safeSetState(nextState, callback) {
		if (!this.isUnmounted) {
			this.setState(nextState, callback);
		}
	}

	handleLoadedMetadata() {
		if (!this.audioRef) {
			return;
		}

		const duration = Number.isFinite(this.audioRef.duration)
			? this.audioRef.duration
			: 0;

		this.safeSetState({
			currentTime: 0,
			duration,
		});
	}

	handleError() {
		this.safeSetState({
			isPlaying: false,
			isDone: true,
		});

		cancelAnimationFrame(this.animationFrame);

		if (typeof this.props.onErrorPlay === 'function') {
			this.props.onErrorPlay();
		}
	}

	handleEnded() {
		this.safeSetState({
			isPlaying: false,
			isDone: true,
			currentTime: this.state.duration,
		});

		cancelAnimationFrame(this.animationFrame);

		if (typeof this.props.onComplete === 'function') {
			this.props.onComplete();
		}
	}

	pauseOtherAudioRefs() {
		const {refs, id = this.uniqueId} = this.props;

		if (!refs) {
			return;
		}

		Object.keys(refs).forEach((key) => {
			const audio = refs[key];

			if (key !== String(id) && audio && !audio.paused) {
				try {
					audio.pause();
				} catch (error) {
					// Ignore pause errors from stale refs.
				}
			}
		});
	}

	pauseCurrentAudio() {
		if (!this.audioRef) {
			return;
		}

		try {
			this.audioRef.pause();
		} catch (error) {
			// Ignore pause errors.
		}

		cancelAnimationFrame(this.animationFrame);

		this.safeSetState({
			isPlaying: false,
		});
	}

	handleStartPlayer() {
		if (this.props.disabled || !this.audioRef) {
			return;
		}

		const audio = this.audioRef;

		this.pauseOtherAudioRefs();

		if (!audio.paused && !audio.ended) {
			this.pauseCurrentAudio();
			return;
		}

		if (audio.ended) {
			audio.currentTime = 0;
		}

		const requestId = this.playRequestId + 1;
		this.playRequestId = requestId;

		const playPromise = audio.play();

		if (playPromise && typeof playPromise.then === 'function') {
			playPromise.catch((error) => {
				if (this.isUnmounted || requestId !== this.playRequestId) {
					return;
				}

				console.warn('Audio play failed:', error);

				this.safeSetState({
					isPlaying: false,
					isDone: true,
				});

				if (typeof this.props.onErrorPlay === 'function') {
					this.props.onErrorPlay(error);
				}
			});
		}
	}

	handlePlay() {
		this.safeSetState(
			{
				isDone: false,
				isPlaying: true,
				isStarted: true,
			},
			() => {
				cancelAnimationFrame(this.animationFrame);
				this.tick();
			}
		);

		if (typeof this.props.onStart === 'function') {
			this.props.onStart();
		}
	}

	handlePause() {
		if (this.isUnmounted) {
			return;
		}

		cancelAnimationFrame(this.animationFrame);

		this.safeSetState({
			isPlaying: false,
		});

		if (typeof this.props.onPause === 'function') {
			this.props.onPause();
		}
	}

	tick() {
		if (this.isUnmounted || !this.audioRef) {
			return;
		}

		const audioRef = this.audioRef;

		if (audioRef.ended || audioRef.paused) {
			this.safeSetState({
				isPlaying: false,
				isDone: true,
			});
			return;
		}

		this.safeSetState({
			currentTime: audioRef.currentTime || 0,
		});

		this.animationFrame = requestAnimationFrame(this.tick);
	}

	render() {
		const {
			disabled,
			id = this.uniqueId,
			inline,
			refs,
			src,
			noBorder,
		} = this.props;

		const {currentTime, duration, isPlaying} = this.state;

		let progress = 0;

		if (isPlaying && duration > 0) {
			const percentageDone = (currentTime * 100) / duration;
			progress =
				DASHARRAY_LENGTH - (percentageDone * DASHARRAY_LENGTH) / 100;
		}

		return (
			<button
				className={classNames({
					'lit-circle': true,
					'lit-audio': true,
					'is-playing': isPlaying,
					inline,
					noBorder,
				})}
				disabled={disabled}
				onClick={(event) => {
					event.preventDefault();
					this.handleStartPlayer();
				}}
				title={disabled ? strings.audioMax : null}
				type="button"
			>
				<svg
					className="lit-circle__interactive"
					height="90"
					style={{enableBackground: 'new 0 0 90 90'}}
					version="1.1"
					viewBox="0 0 90 90"
					width="90"
					x="0px"
					xmlSpace="preserve"
					xmlns="http://www.w3.org/2000/svg"
					xmlnsXlink="http://www.w3.org/1999/xlink"
					y="0px"
				>
					<g transform="rotate(-84 45 45)">
						<circle
							className="lit-audio__outline"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							stroke="rgba(0,0,0,0.1)"
							strokeLinecap="round"
							strokeWidth="5"
						/>
						<circle
							className="lit-audio__progress"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							stroke="#fff"
							strokeLinecap="round"
							strokeWidth="6"
							style={{
								strokeDashoffset: `${progress}`,
							}}
						/>
					</g>
				</svg>

				<div className="lit-audio__icon" />

				<audio
					onLoadedMetadata={this.handleLoadedMetadata}
					onEnded={this.handleEnded}
					onPause={this.handlePause}
					onPlay={this.handlePlay}
					onError={this.handleError}
					preload="auto"
					ref={(ref) => {
						this.audioRef = ref;

						if (refs && id) {
							if (ref) {
								refs[id] = ref;
							} else {
								delete refs[id];
							}
						}
					}}
					src={src}
				/>
			</button>
		);
	}
}

export default AudioPlayer;